import React, { useMemo, useRef, useState } from 'react';
import { Bold, Eye, Heading, List, PencilLine } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { htmlToText, textToHtml } from './lessonText';

// The preview is the student's view of the text, drawn in a sandboxed frame: the HTML of a lesson
// someone else wrote never runs a script in a staff member's signed-in CRM tab.
const PREVIEW_CSS = 'body{margin:0;padding:14px 16px;font:15px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;'
  + 'color:#1c1c1e;background:#fff}h2,h3,h4{font-size:17px;margin:0 0 8px}p{margin:0 0 12px}'
  + 'ul,ol{margin:0 0 12px;padding-left:22px}a{color:#8a6800}img{max-width:100%}.empty{color:#75757a}';

// Puts "**" around the selection (or around a sample word to type over).
const wrapBold = (sample) => (value, start, end) => {
  const selected = value.slice(start, end) || sample;
  return {
    next: `${value.slice(0, start)}**${selected}**${value.slice(end)}`,
    selStart: start + 2,
    selEnd: start + 2 + selected.length,
  };
};

// Starts every selected line (or the line the cursor is on) with `prefix`, once.
const prefixLines = (prefix) => (value, start, end) => {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  let lineEnd = value.indexOf('\n', end);
  if (lineEnd < 0) lineEnd = value.length;
  const block = value.slice(lineStart, lineEnd)
    .split('\n')
    .map((line) => (line.startsWith(prefix) ? line : `${prefix}${line}`))
    .join('\n');
  return {
    next: `${value.slice(0, lineStart)}${block}${value.slice(lineEnd)}`,
    selStart: lineStart,
    selEnd: lineStart + block.length,
  };
};

/**
 * The lesson text: plain text with a few easy rules (see lessonText.js) and a preview of what the
 * student sees. Text that has formatting the plain editor cannot keep opens as HTML instead.
 * Uncontrolled: mount it with a new `key` for another lesson. `onChange` receives the HTML to store.
 */
function LessonContentField({ id, initialHtml, onChange }) {
  const { t } = useI18n();
  const initial = useMemo(() => htmlToText(initialHtml), [initialHtml]);
  const [mode] = useState(initial.plain ? 'text' : 'html');
  const [value, setValue] = useState(initial.plain ? initial.text : (initialHtml || ''));
  const [preview, setPreview] = useState(false);
  const areaRef = useRef(null);

  const html = mode === 'text' ? textToHtml(value) : value;

  const update = (next) => {
    setValue(next);
    onChange(mode === 'text' ? textToHtml(next) : next);
  };

  const applyEdit = (edit) => {
    const el = areaRef.current;
    if (!el) return;
    const { next, selStart, selEnd } = edit(value, el.selectionStart, el.selectionEnd);
    update(next);
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    });
  };

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_CSS}</style></head><body>${
    html || `<p class="empty">${t('lessonAuthoring.content.previewEmpty')}</p>`
  }</body></html>`;

  return (
    <div className="les-content">
      <div className="les-content-bar">
        <div className="les-content-tools" role="toolbar" aria-label={t('lessonAuthoring.content.toolbar')}>
          {mode === 'text' && !preview && (
            <>
              <button type="button" className="ui-icon-btn" title={t('lessonAuthoring.content.bold')} aria-label={t('lessonAuthoring.content.bold')} onClick={() => applyEdit(wrapBold(t('lessonAuthoring.content.boldSample')))}>
                <Bold aria-hidden="true" />
              </button>
              <button type="button" className="ui-icon-btn" title={t('lessonAuthoring.content.list')} aria-label={t('lessonAuthoring.content.list')} onClick={() => applyEdit(prefixLines('- '))}>
                <List aria-hidden="true" />
              </button>
              <button type="button" className="ui-icon-btn" title={t('lessonAuthoring.content.heading')} aria-label={t('lessonAuthoring.content.heading')} onClick={() => applyEdit(prefixLines('## '))}>
                <Heading aria-hidden="true" />
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          className="ds-btn ds-btn-secondary ds-btn-sm"
          aria-pressed={preview}
          onClick={() => setPreview((v) => !v)}
        >
          {preview ? <PencilLine aria-hidden="true" /> : <Eye aria-hidden="true" />}
          {preview ? t('lessonAuthoring.content.write') : t('lessonAuthoring.content.preview')}
        </button>
      </div>
      {preview ? (
        <iframe
          className="les-content-preview"
          title={t('lessonAuthoring.content.previewTitle')}
          sandbox=""
          srcDoc={srcDoc}
        />
      ) : (
        <textarea
          id={id}
          ref={areaRef}
          className={mode === 'html' ? 'les-content-html' : undefined}
          value={value}
          onChange={(e) => update(e.target.value)}
          rows={8}
          placeholder={t('lessonAuthoring.content.placeholder')}
          spellCheck={mode === 'text'}
        />
      )}
      <p className="ui-hint">
        {mode === 'html' ? t('lessonAuthoring.content.htmlMode') : t('lessonAuthoring.content.hint')}
      </p>
    </div>
  );
}

export default LessonContentField;
